import { createFeatureSelector, createSelector } from '@ngrx/store'
import { SELECTED_ITEM_FEATURE_KEY, SelectedItemState } from './selected-item.reducer'
import * as ItemsSelectors from 'libs/gb-dashboard/item/data-access/src/lib/+state/items.selectors'
import { isEmpty } from 'lodash'
import { ItemsEntity, SlimBiddingPurchasesEntity, WinningBidEntity } from './selected-item.models'

export const getSelectedItemState = createFeatureSelector<SelectedItemState>(SELECTED_ITEM_FEATURE_KEY)

export const getSelectedItemLoaded = createSelector(getSelectedItemState, (state: SelectedItemState) => state.loaded)

export const getSelectedItemSaving = createSelector(getSelectedItemState, (state: SelectedItemState) => state.saving)

export const getSelectedItem = createSelector(getSelectedItemState, (state: SelectedItemState) => state.item)

export const getHighestBids = createSelector(
  getSelectedItem,
  (selectedItem: ItemsEntity | null): WinningBidEntity[] => {
    if (!selectedItem?.bidding_item) {
      return []
    }

    const biddingItem = selectedItem?.bidding_item
    const winningBids = biddingItem?.winning_bids

    if (!winningBids) {
      return []
    }

    const reserveEnabled = biddingItem?.reserve_price_enabled
    const reservePrice = biddingItem?.reserve_price

    return winningBids.filter(
      (bid) => !bid.is_pre_bid && (!reserveEnabled || reservePrice == null || bid.amount > reservePrice),
    )
  },
)

export const getBiddingPurchases = createSelector(
  getSelectedItem,
  (selectedItem: ItemsEntity | null): SlimBiddingPurchasesEntity[] =>
    selectedItem?.bidding_item?.bidding_purchases ?? [],
)

export const getSelectedItemId = createSelector(
  getSelectedItemState,
  (state: SelectedItemState) => state.item?.uuid || '',
)

export const getSelectedItemImages = createSelector(
  getSelectedItemState,
  (state: SelectedItemState) => state.item?.images || [],
)

export const getSelectedItemIndexFromList = createSelector(
  ItemsSelectors.selectAllItems,
  getSelectedItemId,
  (allItems, selectedItemId) => allItems.findIndex((item) => item.uuid === selectedItemId),
)

export const getNextFilteredItemId = createSelector(
  ItemsSelectors.selectAllItems,
  getSelectedItemIndexFromList,
  (allItems, selectedItemIndex) =>
    selectedItemIndex !== -1 && selectedItemIndex < allItems.length - 1
      ? allItems[selectedItemIndex + 1].uuid
      : undefined,
)

export const getNextItemId = createSelector(
  ItemsSelectors.selectCurrentFilters,
  getSelectedItem,
  getNextFilteredItemId,
  (currentFilters, selectedItem, nextFilteredItemId) =>
    !isEmpty(currentFilters) ? nextFilteredItemId : selectedItem?.next_id,
)

export const getPrevFilteredItemId = createSelector(
  ItemsSelectors.selectAllItems,
  getSelectedItemIndexFromList,
  (allItems, selectedItemIndex) => (selectedItemIndex > 0 ? allItems[selectedItemIndex - 1].uuid : undefined),
)

export const getPrevItemId = createSelector(
  ItemsSelectors.selectCurrentFilters,
  getSelectedItem,
  getPrevFilteredItemId,
  (currentFilters, selectedItem, prevFilteredItemId) =>
    !isEmpty(currentFilters) ? prevFilteredItemId : selectedItem?.prev_id,
)
